/**
 * schema.ts — TypeScript types for the Filosofia distributed catalog
 *
 * SINGLE RESPONSIBILITY: models the catalog shipped with the tool.
 *   - User-generated data (lessons, notes, bookmarks) lives in schema-user.ts
 *   - UI state (filter lenses, modal states) lives in src/types/ui.ts
 *   - i18n types (Locale, TranslationKey) live in src/i18n/
 * Do not add anything here that is not part of the distributed catalog.
 *
 * Data lives in two parallel tiers (see ADR 0002):
 *   1. Language-neutral metadata      → autori.json, concetti.json, tappe.json, argomenti.json
 *   2. Editorial content per locale   → autori.{it,en}.json, concetti.{it,en}.json, tappe.{it,en}.json, argomenti.{it,en}.json
 *
 * The service layer (IDataService) merges the two at query time and returns
 * the *Localized types. Components never see the raw tiers directly.
 */

// ══════ ENUMS & CLOSED UNION TYPES ══════

/**
 * The seven fundamental problems prescribed by the Italian ministerial
 * curriculum for high schools (D.I. 211/2010).
 *
 * Closed union: no extensions allowed. New tematiche that do not map to
 * one of the seven go into Philosopher.tag_tematici or Concept.tag_tematici
 * (free-form strings), never extending this type.
 */
export type ProblemaFondamentale =
  | 'ontologia'
  | 'etica-felicita'
  | 'filosofia-religioni'
  | 'scienza-moderna'
  | 'conoscenza'
  | 'bellezza'
  | 'liberta-potere';

/**
 * Runtime constant mirroring the ProblemaFondamentale closed union.
 *
 * Declared `readonly` and typed as the union itself: if a new value is added
 * to the type without being added here (or vice versa), the compiler catches
 * the drift. This is the canonical way to enumerate the seven problems in
 * the UI (program lens filter buttons, tag selectors, reasoning reports).
 *
 * Order is the canonical ministerial order from D.I. 211/2010 and should
 * be preserved — the UI renders the filter buttons in this exact order.
 */
export const PROBLEMI_FONDAMENTALI: readonly ProblemaFondamentale[] = [
  'ontologia',
  'etica-felicita',
  'filosofia-religioni',
  'scienza-moderna',
  'conoscenza',
  'bellezza',
  'liberta-potere',
] as const;

export type AnnoScolastico = 3 | 4 | 5;

/**
 * Licensing classification of a citation source. Must be declared for every
 * Citazione. Used by the UI to show attribution when required by the license.
 */
export type LicenseKind =
  | 'PD'                    // Public domain (author died pre-1955, European rule)
  | 'CC0'                   // Wikidata structural data
  | 'CC-BY'                 // Pievatolo Kant translation (Firenze University Press)
  | 'CC-BY-SA'              // Perseus, Scaife Viewer
  | 'proprietary-eduverse'; // Editorial content written in-house by the team

/**
 * Fonti canoniche autorizzate per i dati del tool Filosofia.
 *
 * UNION CHIUSO PER DISCIPLINA: l'aggiunta di una nuova fonte richiede
 *   (a) modifica esplicita di questo type
 *   (b) verifica della licenza della nuova fonte (deve essere CC0, CC BY,
 *       CC BY-SA, o pubblico dominio)
 *   (c) aggiornamento dell'INDEX agent in filosofia/.claude/agents/INDEX.md
 *       nella sezione "Whitelist fonti dati"
 *   (d) ADR se la nuova fonte introduce vincoli architetturali
 *
 * NON aprire questo type a `| string` per nessun motivo. La compilazione
 * DEVE fallire se qualcuno tenta di usare una fonte non autorizzata.
 * Questo attrito è una feature, non un difetto: protegge il tool dalle
 * violazioni accidentali di licenza.
 */
export type FonteCanonica =
  | 'wikidata'
  | 'perseus'
  | 'bonner-kant-korpus'
  | 'pievatolo-firenze-up'
  | 'project-gutenberg'
  | 'editorial-eduverse';
  // Aggiungere qui SOLO dopo verifica di licenza esplicita (vedi JSDoc sopra)

// ══════ COMMON ══════

/**
 * Canonical textual reference to a passage.
 * Example: "Grundlegung zur Metaphysik der Sitten, AA IV, 446"
 */
export interface RiferimentoCanonico {
  opera_id?: string;          // Link to Opera inside the author record (when tracked)
  titolo: string;             // Human-readable title, original language preferred
  edizione?: string;          // "Akademie-Ausgabe IV", "Bekker", "Stephanus"
  passaggio?: string;         // "446", "1094a1-5", "II.iii.6"
  anno_composizione?: number;
}

export interface FonteTracking {
  wikidata_qid?: string;
  verification?: {
    score: '3/3' | '2/3' | '1/3' | 'n/a';
    checked_at: string;       // ISO 8601
  };
  fetched_at?: string;        // ISO 8601
  notes?: string;
}

// ══════ OPERA (work) ══════

/**
 * A single work of a philosopher. Lives as sub-entity inside Philosopher.opere[].
 * Philosophical works have single authorship almost always; rare contested
 * attributions are handled editorially, not structurally.
 */
export interface Opera {
  id: string;                 // kebab-case, unique within the author: "kritik-der-reinen-vernunft"
  titolo_originale: string;   // Always in original language: "Kritik der reinen Vernunft"
  lingua_originale: string;   // ISO code: "de", "la", "grc", "fr", "it"
  anno_composizione?: number;
  anno_pubblicazione?: number;
  opera_principale: boolean;  // true for the "notable works" shown in the scheda
}

// ══════ PHILOSOPHER ══════

/**
 * Language-neutral metadata of a philosopher. Lives in autori.json.
 * Does NOT contain prose — that is in autori.{it,en}.json.
 */
export interface Philosopher {
  id: string;                          // kebab-case: "kant", "tommaso-daquino"
  wikidata_qid?: string;
  nome: string;                        // Canonical Italian form (display): "Immanuel Kant"
  nome_originale?: string;             // If different from Italian: "René Descartes" (IT: "Cartesio")
  anno_nascita: number;
  anno_morte: number;

  /**
   * Luogo di nascita come stringa libera, toponimo storico preferito.
   *
   * SCELTA PROVVISORIA: i luoghi non sono modellati come entità
   * indipendenti (Luogo con coordinate, denominazione storica/moderna,
   * paese attuale) perché il tool iniziale non ha mappe geografiche
   * né filtri per area. Una stringa è sufficiente.
   *
   * Possibile promozione futura a entità `Luogo` quando e se verranno
   * introdotti componenti geografici. Migrazione dati gestita da agent
   * dedicato che normalizza le stringhe esistenti ai nuovi id.
   */
  luogo_nascita?: string;              // es. "Königsberg"
  luogo_morte?: string;

  opere: Opera[];

  // Biographical relations (resolved to ids in autori.json)
  maestri: string[];
  allievi: string[];
  influenzato_da: string[];
  ha_influenzato: string[];

  // Relations to entities outside the target list (preserved from Wikidata raw)
  _external_relations?: {
    maestri?: ExternalRelationEntry[];
    allievi?: ExternalRelationEntry[];
    influenzato_da?: ExternalRelationEntry[];
    ha_influenzato?: ExternalRelationEntry[];
  };

  // Canonical relations added by philo-relations-curator (ADR 0001)
  relazioni_canoniche?: {
    _source: 'canonical-liceo';
    _added_by: 'philo-relations-curator';
    influenzato_da_aggiuntivi?: RelazioneCanonicaEntry[];
    ha_influenzato_aggiuntivi?: RelazioneCanonicaEntry[];
    maestri_aggiuntivi?: RelazioneCanonicaEntry[];
    allievi_aggiuntivi?: RelazioneCanonicaEntry[];
  };

  /**
   * Scuole filosofiche di appartenenza, come stringhe libere.
   *
   * SCELTA PROVVISORIA: la tassonomia delle scuole filosofiche è fuzzy e
   * non standardizzata tra Wikidata, manuali liceali e storiografie
   * (es. dove finisce "empirismo" e inizia "scetticismo"? Cartesio è
   * "razionalista" o "modernista"?). Tenuta come stringa libera in attesa
   * di vedere le ricorrenze reali nel catalogo popolato.
   *
   * Possibile promozione futura a entità Scuola con id, una volta che
   * il vocabolario si stabilizza. Migrazione gestita da agent dedicato.
   */
  scuola: string[];

  // Ministerial curriculum tagging (ADR 0003)
  anno_scolastico: AnnoScolastico[];
  obbligatorio_ministero: boolean;     // TRUE only for the fixed 15 listed in the program
  problemi_fondamentali_iniziali: ProblemaFondamentale[];
  // NOTE: problemi_fondamentali_effettivi is NOT stored here.
  // It is derived at query time and exposed only in PhilosopherLocalized. ADR 0003.

  // Additional thematic tags that don't map to the seven ministerial problems
  tag_tematici?: string[];

  // Portrait image derived conventionally from id: /images/authors/<id>.webp
  // Remote URLs (Wikimedia) are in scripts/fetch-author-portraits.ts only.

  _sources?: FonteTracking;
}

export interface ExternalRelationEntry {
  wikidata_qid: string;
  label: string;
  reason: 'not_in_target_list' | string;
}

export interface RelazioneCanonicaEntry {
  id: string;                 // Id of the other philosopher in autori.json
  nota: string;               // Short justification citing an opera or passage
  opera_riferimento: string;  // Work citation
}

// ══════ PHILOSOPHER — editorial content per locale ══════

/**
 * Editorial content of a philosopher, per locale.
 * Lives in autori.it.json / autori.en.json. Written by hand by the team.
 * String fields may contain simple HTML (<p>, <em>, <strong>, <a>).
 */
export interface PhilosopherContent {
  id: string;                 // Matches Philosopher.id
  inquadramento: string;
  contesto_generale: string;
  cosa_cambia_filosofia?: string;
}

/**
 * View returned by IDataService.getAuthor(id, locale).
 *
 * `problemi_fondamentali_effettivi` is DERIVED at runtime as:
 *   union of the `problema_fondamentale` tags of this author's written tappe,
 *   with fallback to `problemi_fondamentali_iniziali` when no tappe exist
 *   for a given problem.
 *
 * See ADR 0003 for the ground-truth-derivato rationale.
 */
export type PhilosopherLocalized = Philosopher & {
  content: PhilosopherContent;
  problemi_fondamentali_effettivi: ProblemaFondamentale[];
};

// ══════ CONCEPT ══════

/**
 * A philosophical concept (libertà, alienazione, cogito, sostanza, ...).
 * Independent entity, finer-grained than ProblemaFondamentale.
 * Each concept MUST declare which ministerial problem it belongs to.
 *
 * concetti.json starts empty [] and is populated editorially.
 * The seven ministerial problems are NOT concepts — they are closed-union
 * tags on ProblemaFondamentale.
 */
export interface Concept {
  id: string;                 // kebab-case: "liberta", "alienazione", "cogito"
  nome: string;               // Display: "Libertà"

  problema_fondamentale_associato: ProblemaFondamentale;

  /**
   * Editorial order of tappe that traverse this concept.
   *
   * STORED (not derived), because the order can be editorial, not strictly
   * chronological — a teacher may want Platone before Parmenide for
   * exposition reasons, or Aristotele before Platone in a "sostanza" path.
   *
   * BUILD-TIME VALIDATION (mandatory): every id referenced here must
   * exist in tappe.json. The build fails on orphan ids — `philo-schema-builder`
   * (or a post-merge checker) enforces strict referential integrity.
   */
  tappe_ordinate: string[];   // Array of Tappa.id

  tag_tematici?: string[];
}

export interface ConceptContent {
  id: string;
  descrizione: string;
  note_storiche?: string;
}

export type ConceptLocalized = Concept & {
  content: ConceptContent;
};

// ══════ TAPPA (author × concept intersection) ══════

/**
 * Base unit of the tool: a specific author's position on a specific concept.
 * One tappa per (autore, concetto) couple; multiple canonical citations
 * live inside via the citazioni[] array. See ADR 0001.
 *
 * Id convention: `<autore-id>_<concetto-id>` (e.g. "kant_liberta").
 */
export interface Tappa {
  id: string;
  autore_id: string;          // FK → Philosopher.id
  concetto_id: string;        // FK → Concept.id

  anno_riferimento: number;   // Year of the reference work, anchors ordering

  argomento_id?: string;      // FK → Argomento.id (cogito, cinque-vie, ecc.)

  /**
   * Editorial ground-truth tag (ADR 0003).
   * Wins over Philosopher.problemi_fondamentali_iniziali when computing
   * problemi_fondamentali_effettivi at query time.
   */
  problema_fondamentale: ProblemaFondamentale;

  /**
   * Canonical citations (metadata only; text lives in TappaContent.citazioni).
   *
   * MERGE BY ID, NOT BY INDEX: each Citazione has an explicit `id` used as
   * the join key with the TappaContent.citazioni array. Adding a citation
   * mid-array in the locale file must not break the merge.
   *
   * Id convention: `<tappa-id>_<short-slug>` where the slug describes the
   * source work (e.g. "kant_liberta_grundlegung", "kant_liberta_pace_perpetua").
   * Descriptive slugs, never sequential numbers.
   */
  citazioni: Citazione[];
}

export interface Citazione {
  id: string;                 // Join key with CitazioneContent.id — descriptive slug
  riferimento: RiferimentoCanonico;
  lingua_originale: string;   // "de", "la", "grc"
  license: LicenseKind;
  attribution?: string;       // REQUIRED for CC-BY / CC-BY-SA
  fonte: FonteCanonica;
}

/**
 * Editorial content of a tappa, per locale. Lives in tappe.{it,en}.json.
 * The citazioni array is matched with Tappa.citazioni BY ID, not by index.
 */
export interface TappaContent {
  id: string;                 // Matches Tappa.id
  spiegazione: string;
  contesto_storico: string;
  cosa_cambia: string;
  /**
   * Riassunto ≤120 char per i NODI-MAPPA (ConceptMap / AuthorMap): una frase
   * sensata scritta apposta che coglie il trattamento dell'autore su quel
   * concetto. NON è troncamento della prosa. No HTML. Opzionale: se assente,
   * la mappa usa un fallback (shortSummary della spiegazione).
   */
  map_blurb?: string;
  citazioni: CitazioneContent[];
}

export interface CitazioneContent {
  id: string;                 // Must match a Citazione.id in the twin Tappa
  testo_originale: string;    // Greek / Latin / German / original tongue
  testo_tradotto: string;     // Translation in this locale
}

export type TappaLocalized = Tappa & {
  content: TappaContent;
};

// ══════ ARGOMENTO (named canonical argument) ══════

/**
 * A canonical philosophical argument, identified by name.
 * Examples: cogito, cinque vie di Tommaso, imperativo categorico,
 * scommessa di Pascal, argomento ontologico di Anselmo.
 *
 * Independent entity because a single argument can be referenced by
 * multiple tappe across different concepts.
 */
export interface Argomento {
  id: string;                 // "cogito", "imperativo-categorico", "cinque-vie"
  nome: string;               // Display: "Cogito ergo sum"
  autore_id: string;          // FK → Philosopher.id
  opera_id?: string;          // FK → Opera inside the author (when tracked)
  opera_titolo: string;       // Fallback string, always present — graceful UI degradation
  anno_formulazione?: number;
  concetti_toccati: string[]; // FK → Concept.id

  /**
   * Bilingual citations tied to the argument — same pattern as Tappa.citazioni.
   * Metadata only (riferimento, license, language). Textual content
   * (original + translation) lives in ArgomentoContent.citazioni, merged by id.
   *
   * Id convention: `<argomento-id>_<short-slug>` (e.g. "cogito_meditationes-ii").
   * Target: 2-4 citations per argument.
   */
  citazioni: Citazione[];

  /**
   * Historical canonical objections — structural metadata (who, where).
   * The objection's editorial text lives in ArgomentoContent.obiezioni_storiche,
   * matched by id.
   *
   * Target: 2-3 objections per argument, always attributed to a historical
   * philosopher (never anonymous "some critics").
   */
  obiezioni: ObiezioneCanonica[];
}

/**
 * Structural metadata for a historical objection against an Argomento.
 * The objection's textual unpacking lives in ArgomentoContent.obiezioni_storiche
 * (matched by id). Optionally may link to a dedicated tappa that unpacks
 * the objection in depth.
 */
export interface ObiezioneCanonica {
  id: string;                 // Slug descrittivo: "gassendi-vs-cogito", "hume-vs-cinque-vie"
  /**
   * FK → Philosopher.id. Optional because some historical critics
   * (e.g. Gassendi, Gaunilo) may live outside the 52-author target list.
   * When absent, ObiezioneContent.autore_nome is the display fallback.
   */
  autore_id?: string;
  riferimento: RiferimentoCanonico;
  tappa_id?: string;          // Optional deep-link to a tappa
}

// ══════ ARGOMENTO EDITORIAL CONTENT ══════

/**
 * Editorial content of an Argomento, per locale. Lives in argomenti.{it,en}.json.
 *
 * Structure mirrors the 5-section editorial format agreed for the Filosofia
 * tool (see .agent/editorial/ARGOMENTI-STYLE.md):
 *   1. inquadramento           (~200-250 words)
 *   2. argomento_passo_passo   (~900-1100 words: premesse numerate + conclusione)
 *   3. punto_decisivo          (~150-200 words)
 *   4. obiezioni_storiche      (2-3 entries, ~400-500 words total)
 *   5. eredita                 (~150-200 words)
 *
 * Target total: 1800-2200 words per argument. Coherence across the 10
 * arguments is more important than per-argument depth.
 *
 * Merge rules:
 *   - citazioni and obiezioni_storiche are merged with the twin Argomento
 *     arrays BY ID, not by index.
 */
export interface ArgomentoContent {
  id: string;                 // Matches Argomento.id

  enunciato: string;          // 1 sentence, display-ready: "Penso, dunque esisto."

  inquadramento: string;              // Sezione 1
  argomento_passo_passo: ArgomentoPassoPasso;  // Sezione 2
  punto_decisivo: string;             // Sezione 3
  obiezioni_storiche: ObiezioneContent[];      // Sezione 4 (merged by id)
  eredita: string;                    // Sezione 5

  citazioni: CitazioneContent[];      // Merged by id with Argomento.citazioni
}

/**
 * The logical core of the argument: ordered premises with commentary,
 * plus the concluding step. Each premise carries its own didactic gloss.
 */
export interface ArgomentoPassoPasso {
  introduzione?: string;      // Optional bridge from inquadramento
  premesse: PremessaArgomento[];
  conclusione: string;        // How and why the conclusion follows
  /**
   * LIM-only: 1-sentence recap of the conclusion (≤120 chars).
   * Shown in ArgumentMapView when the conclusion box is collapsed.
   * Optional — when missing, LIM falls back to a runtime truncation.
   * Desktop views ignore this field entirely.
   */
  conclusione_sintesi?: string;
}

export interface PremessaArgomento {
  numero: number;             // 1, 2, 3… — preserved for display (P1, P2, P3)
  enunciato: string;          // Short propositional statement
  commento: string;           // Didactic paragraph: meaning, motivation, implications
  /**
   * LIM-only: 1-sentence recap of this premise (≤120 chars).
   * Shown in ArgumentMapView when the premise box is collapsed.
   * Optional — when missing, LIM falls back to a runtime truncation of
   * `enunciato`. Desktop views ignore this field entirely.
   */
  sintesi?: string;
}

/**
 * Editorial text of a historical objection.
 * Matched with Argomento.obiezioni by id.
 */
export interface ObiezioneContent {
  id: string;                 // Must match an ObiezioneCanonica.id in the twin Argomento
  autore_nome: string;        // Display fallback (e.g. "Pierre Gassendi")
  anno?: number;
  opera?: string;             // Display string: "Obiezioni alle Meditazioni"
  obiezione: string;          // Discursive exposition of the objection
  risposta?: string;          // Author's reply, if historically recorded
}

export type ArgomentoLocalized = Argomento & {
  content: ArgomentoContent;
};
